/**
 * Web Vitals Reporting for Freedom Contracting Group
 * 
 * This module tracks Core Web Vitals metrics which are crucial for:
 * - Google Search ranking (SEO)
 * - User experience monitoring
 * - Performance optimization
 * 
 * Metrics tracked:
 * - CLS (Cumulative Layout Shift) - visual stability
 * - INP (Interaction to Next Paint) - responsiveness
 * - LCP (Largest Contentful Paint) - loading performance
 * - FCP (First Contentful Paint) - perceived load speed
 * - TTFB (Time to First Byte) - server response time
 */

// Thresholds based on Google's Core Web Vitals guidelines
const THRESHOLDS = {
  CLS: { good: 0.1, needsImprovement: 0.25 },
  INP: { good: 200, needsImprovement: 500 },
  LCP: { good: 2500, needsImprovement: 4000 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 }
};

/**
 * Get rating based on metric value
 */
const getRating = (name, value) => {
  const threshold = THRESHOLDS[name];
  if (!threshold) return 'unknown';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
};

/**
 * Format metric for logging
 */
const formatMetric = (metric) => {
  const rating = getRating(metric.name, metric.value);
  const ratingEmoji = rating === 'good' ? 'âœ…' : rating === 'needs-improvement' ? 'âš ï¸' : 'âŒ';
  
  return {
    name: metric.name,
    value: metric.value,
    rating,
    ratingEmoji,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType
  };
};

/**
 * Send metrics to analytics endpoint
 */
const sendToAnalytics = (metric) => {
  const formattedMetric = formatMetric(metric);
  
  // Console logging for development
  if (process.env.NODE_ENV === 'development') {
    console.log(
      `${formattedMetric.ratingEmoji} Web Vital: ${formattedMetric.name}`,
      `| Value: ${formattedMetric.value.toFixed(2)}`,
      `| Rating: ${formattedMetric.rating}`
    );
  }

  // Send to GTM dataLayer (works with Google Tag Manager)
  // Variable names match GTM setup: metric_name, metric_value, metric_rating
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'web_vitals',
      metric_name: metric.name,
      metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_rating: formattedMetric.rating,
      metric_id: metric.id,
      metric_delta: Math.round(metric.delta)
    });
  }

  // Also send via gtag if available (direct GA4 installation)
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
      metric_rating: formattedMetric.rating,
    });
  }
};

/**
 * Main function to report Web Vitals
 * @param {Function} onPerfEntry - Optional callback for each metric
 */
const reportWebVitals = (onPerfEntry) => {
  import('web-vitals').then((webVitals) => {
    // Get the metric functions - web-vitals v4+ exports them directly
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = webVitals;
    
    const handleMetric = (metric) => {
      sendToAnalytics(metric);
      if (onPerfEntry && typeof onPerfEntry === 'function') {
        onPerfEntry(metric);
      }
    };

    // Cumulative Layout Shift - measures visual stability
    if (onCLS) onCLS(handleMetric);
    
    // Interaction to Next Paint - measures responsiveness (replaced FID)
    if (onINP) onINP(handleMetric);
    
    // Largest Contentful Paint - measures loading performance
    if (onLCP) onLCP(handleMetric);
    
    // First Contentful Paint - measures perceived load speed
    if (onFCP) onFCP(handleMetric);
    
    // Time to First Byte - measures server response time
    if (onTTFB) onTTFB(handleMetric);
  }).catch((err) => {
    console.warn('Failed to load web-vitals:', err);
  });
};

export default reportWebVitals;

// Export individual functions for custom use
export { sendToAnalytics, formatMetric, getRating, THRESHOLDS };